import React, { useState, useCallback } from 'react';
import styled, { css } from '@emotion/native';
import { KeyboardAvoidingView } from 'react-native';
import { useMutation } from '@apollo/client';

import Modal from '@/shared/components/Modal';
import Button from '@/shared/components/Button';
import { BaseText, BodyText, H2 } from '@/shared/components/StyledText';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';
import useStore from '@/stores/useStore';
import StaticInput from '@/shared/components/StaticInput';
import { TextInput, Portal } from 'react-native-paper';

import { green, sand, red } from '@/shared/styles/colors';
import type { Consultation } from '@/shared/types/generated';
import { END_IN_PERSON_SESSION } from '@/shared/mutations';
import type {
  MutationResult,
  MutationEndInPersonSessionArgs,
} from '@/shared/types/generated';
import { IsDesktopProps } from '@/shared/styles/types';
import useBreakpoint from '@/shared/hooks/useBreakpoint';

/** Extra time allowed = half of scheduled duration (matches 1.5× Stripe pre-auth). */
const IN_PERSON_EXTRA_TIME_MULTIPLIER = 0.5;

interface InPersonCompletionModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  consultation: Consultation;
}

const InPersonCompletionModal: React.FC<InPersonCompletionModalProps> = ({
  isVisible,
  setIsVisible,
  consultation,
}) => {
  const { isDesktop } = useBreakpoint();
  const {
    setSnackbarState,
    setIsPostCallModalVisible,
    setCompletedSessionDuration,
    setEstimatedSessionDuration,
  } = useStore();
  const [extraTimeInput, setExtraTimeInput] = useState('');

  const scheduledDuration = consultation.estimatedDuration ?? 0;
  const maxExtraMinutes = Math.round(
    scheduledDuration * IN_PERSON_EXTRA_TIME_MULTIPLIER,
  );
  const parsedExtraTime = parseInt(extraTimeInput, 10);
  const extraTimeMinutes = isNaN(parsedExtraTime) ? 0 : parsedExtraTime;
  // Empty input is valid — means no extra time. Only flag non-empty invalid values.
  const isExtraTimeInvalid =
    extraTimeInput !== '' &&
    (isNaN(parsedExtraTime) ||
      parsedExtraTime < 0 ||
      parsedExtraTime > maxExtraMinutes);

  const [endInPersonSession, { loading }] = useMutation<
    MutationResult<'endInPersonSession'>,
    MutationEndInPersonSessionArgs
  >(END_IN_PERSON_SESSION, {
    onCompleted: (data) => {
      if (data?.endInPersonSession) {
        const totalDurationMinutes = scheduledDuration + extraTimeMinutes;

        setCompletedSessionDuration(totalDurationMinutes * 60);
        setEstimatedSessionDuration(scheduledDuration);

        setSnackbarState({
          message: 'In-person session completed successfully!',
          open: true,
          severity: 'success',
        });

        setIsVisible(false);
        setIsPostCallModalVisible(true);
      }
    },
    onError: () => {
      setSnackbarState({
        message: 'Failed to complete session. Please try again.',
        open: true,
        severity: 'error',
      });
    },
  });

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setExtraTimeInput('');
  }, [setIsVisible]);

  const handleConfirmComplete = useCallback(() => {
    if (!consultation.id) {
      setSnackbarState({
        message: 'Unable to complete session - consultation ID not found',
        open: true,
        severity: 'error',
      });
      return;
    }

    endInPersonSession({
      variables: {
        input: {
          consultationId: consultation.id,
          extraTimeMinutes: extraTimeMinutes > 0 ? extraTimeMinutes : undefined,
        },
      },
    });
  }, [consultation.id, extraTimeMinutes, endInPersonSession, setSnackbarState]);

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onDismiss={handleClose}
      onRequestClose={handleClose}
      style={isDesktop ? { paddingVertical: 40 } : undefined}
    >
      <Portal.Host>
        <ModalWrapper style={containerStyle} isScrollable>
          <CloseRow>
            <CloseButton
              onPress={handleClose}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel="Close modal"
              accessibilityHint="Closes the session completion modal"
            >
              <CloseIcon
                source={require('@/assets/images/CloseIcon.png')}
                tintColor={sand}
              />
            </CloseButton>
          </CloseRow>
          <KeyboardAvoidingView
            behavior="padding"
            style={{ flex: 1, width: '100%', alignItems: 'center' }}
          >
            <OuterContentContainer isDesktop={isDesktop}>
              <HeaderSection>
                <Title>
                  Complete {'\n'} In-Person{!isDesktop && '\n'} Session
                  {isDesktop && '\n'}
                </Title>
              </HeaderSection>

              <ContentSection>
                <InfoText style={{ fontWeight: '500' }}>
                  Confirm the final duration of the in-person session, include
                  any additional time to update the client&apos;s charge.
                </InfoText>
                <InfoText style={{ fontWeight: 'bold' }}>
                  Scheduled Session Duration: {scheduledDuration} minutes
                </InfoText>

                <ExtraTimeSection>
                  <AdditionalTimeLabel>Additional Time:</AdditionalTimeLabel>
                  <StaticInput
                    labelText=""
                    labelStyle={{ display: 'none' }}
                    accessibilityLabel="Additional time in minutes"
                    accessibilityHint="Enter extra session time in minutes"
                    value={extraTimeInput}
                    onChange={(e) =>
                      setExtraTimeInput(e.nativeEvent.text.replace(/\D/g, ''))
                    }
                    placeholderText="0"
                    keyboardType="number-pad"
                    right={
                      <TextInput.Affix
                        text="minutes"
                        textStyle={{ color: green }}
                      />
                    }
                    customStyles={inputStyle}
                  />
                  {isExtraTimeInvalid && (
                    <ValidationText>
                      Max extra time is {maxExtraMinutes} min (half of booked
                      time)
                    </ValidationText>
                  )}
                </ExtraTimeSection>
              </ContentSection>

              <BottomContainer>
                <Button
                  text="Mark Session as Completed"
                  variant="secondary"
                  onPress={handleConfirmComplete}
                  loading={loading}
                  disabled={loading || isExtraTimeInvalid}
                  customStyle={{ marginBottom: 27, minWidth: 322 }}
                />
                <DisclaimerText>
                  Please only add time if the session genuinely went over.
                  Clients will be charged accordingly.
                </DisclaimerText>
              </BottomContainer>
            </OuterContentContainer>
          </KeyboardAvoidingView>
        </ModalWrapper>
      </Portal.Host>
    </Modal>
  );
};

export default InPersonCompletionModal;

const containerStyle = css({
  flex: 1,
  backgroundColor: green,
  paddingHorizontal: 30,
  paddingBottom: 20,
  width: '100%',
  borderRadius: 12,
});

const CloseRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  width: '100%',
  paddingTop: 20,
  paddingRight: 0,
  marginBottom: 8,
});

const CloseButton = styled.TouchableOpacity({
  padding: 8,
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
  resizeMode: 'contain',
});

const HeaderSection = styled.View({
  alignItems: 'center',
  marginBottom: 16,
  marginTop: 8,
});

const Title = styled(H2)({
  color: sand,
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: 0,
  marginBottom: 8,
  fontWeight: 'regular',
});

const ContentSection = styled.View({
  width: '100%',
  alignItems: 'center',
  maxWidth: 353,
});

const InfoText = styled(BodyText)({
  color: sand,
  fontSize: 15,
  lineHeight: 20,
  textAlign: 'center',
  marginBottom: 12,
  paddingHorizontal: 16,
});

const DisclaimerText = styled(BaseText)({
  color: sand,
  fontSize: 15,
  textAlign: 'center',
  marginBottom: 20,
});

const ExtraTimeSection = styled.View({
  marginBottom: 20,
  width: '100%',
  alignItems: 'center',
});

const AdditionalTimeLabel = styled(BodyText)({
  color: sand,
  fontSize: 14,
  marginBottom: 10,
  textAlign: 'center',
});

const ValidationText = styled(BaseText)({
  color: red,
  fontSize: 12,
  marginTop: 6,
  paddingHorizontal: 4,
});

const BottomContainer = styled.View({
  width: '100%',
  alignItems: 'center',
  maxWidth: 353,
  marginTop: 4,
});

const OuterContentContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexDirection: 'column',
  width: isDesktop ? 620 : '100%',
  alignItems: 'center',
  alignSelf: 'center',
}));

const inputStyle = css({
  width: 180,
});
